// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useHoverCard.ts
 * @input Uses useLayer, React hooks
 * @output Exports useHoverCard hook for hover/focus triggered layers
 * @position Layer hook; builds on useLayer for hover card behavior
 *
 * SYNC: When modified, update:
 * - /packages/core/src/HoverCard/index.ts
 */
import { useCallback, useEffect, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useLayer } from "../Layer/useLayer.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import "../theme/tokens.stylex.js";
import { colorVars, shadowVars, radiusVars, spacingVars } from "../theme/tokens.stylex.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  container: {
    kWkggS: "x10xzikg",
    "--_hovercard-radius": "xqwz7mz",
    kaIpWk: "x7a53xk",
    kGVxlE: "x14hfi27",
    $$css: true
  }
};

/**
 * Focus trigger behavior for hover cards
 */

/**
 * Check if an element is naturally focusable
 */
function isFocusable(element) {
  // Elements with explicit tabindex
  if (element.hasAttribute('tabindex')) {
    return element.tabIndex >= 0;
  }

  // Naturally focusable elements
  const focusableTags = ['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA'];
  if (focusableTags.includes(element.tagName)) {
    return !element.disabled;
  }

  // Elements with contenteditable
  if (element.isContentEditable) {
    return true;
  }
  return false;
}

/**
 * Hook for hover card behavior with hover/focus triggers.
 *
 * Builds on useLayer to add:
 * - Hover triggers with configurable delay
 * - Focus triggers with auto-detection for focusable elements
 * - Stay-open behavior when mouse/focus moves into the hover card
 *
 * @example
 * ```
 * const hoverCard = useHoverCard({ placement: 'above' });
 * <Button ref={hoverCard.ref} aria-describedby={hoverCard.describedBy}>
 *   Hover me
 * </Button>
 * {hoverCard.renderHoverCard(<ProfileCard user={user} />)}
 * ```
 */
export function useHoverCard(options = {}) {
  const {
    placement = 'above',
    alignment = 'center',
    delay = 300,
    hideDelay = 200,
    focusTrigger = 'auto',
    isEnabled = true,
    label,
    isOpen,
    isDefaultOpen = false,
    onShow,
    onHide
  } = options;
  const layer = useLayer({
    mode: 'context',
    lazyMount: true,
    onShow,
    onHide
  });
  const popoverXstyle = styles.container;
  const showTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const triggerRef = useRef(null);
  const isHoveringContentRef = useRef(false);
  // Track when we're dismissing via Escape to prevent re-show on refocus
  const isEscapeDismissingRef = useRef(false);

  // Clear all timeouts
  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // Schedule show with delay (suppressed when isOpen is false)
  const scheduleShow = useCallback(() => {
    if (!isEnabled || isOpen === false) {
      return;
    }
    clearTimeouts();
    showTimeoutRef.current = setTimeout(() => {
      layer.show();
    }, delay);
  }, [isEnabled, isOpen, clearTimeouts, layer, delay]);

  // Schedule hide with delay (suppressed when isOpen is true)
  const scheduleHide = useCallback(() => {
    if (isOpen === true) {
      return;
    }
    clearTimeouts();
    hideTimeoutRef.current = setTimeout(() => {
      // Don't hide if hovering content
      if (!isHoveringContentRef.current) {
        layer.hide();
      }
    }, hideDelay);
  }, [isOpen, clearTimeouts, layer, hideDelay]);

  // Event handlers
  const handleMouseEnter = useCallback(() => {
    scheduleShow();
  }, [scheduleShow]);
  const handleMouseLeave = useCallback(() => {
    scheduleHide();
  }, [scheduleHide]);
  const handleFocusIn = useCallback(() => {
    if (!isEnabled) {
      return;
    }
    // Skip showing if we're in the middle of an Escape dismiss
    if (isEscapeDismissingRef.current) {
      isEscapeDismissingRef.current = false;
      return;
    }
    clearTimeouts();
    layer.show();
  }, [isEnabled, clearTimeouts, layer]);
  const handleFocusOut = useCallback(e => {
    // Check if focus is moving to the hover card content
    const relatedTarget = e.relatedTarget;
    const popoverElement = document.getElementById(layer.id);
    if (popoverElement?.contains(relatedTarget)) {
      // Focus moving into hover card, keep it open
      return;
    }
    scheduleHide();
  }, [layer.id, scheduleHide]);
  const handleKeyDown = useCallback(e => {
    if (e.key === 'Escape') {
      // Stop propagation so parent components don't react to the same Escape
      e.stopPropagation();
      // Hide immediately without refocusing (we're already on trigger)
      clearTimeouts();
      layer.hide();
    }
  }, [clearTimeouts, layer]);

  // Interaction ref that handles event listeners only
  const interactionRef = useCallback(el => {
    // Cleanup previous element
    if (triggerRef.current) {
      triggerRef.current.removeEventListener('mouseenter', handleMouseEnter);
      triggerRef.current.removeEventListener('mouseleave', handleMouseLeave);
      triggerRef.current.removeEventListener('focusin', handleFocusIn);
      triggerRef.current.removeEventListener('focusout', handleFocusOut);
      triggerRef.current.removeEventListener('keydown', handleKeyDown);
    }
    if (el) {
      // Attach hover listeners
      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);

      // Attach focus listeners based on focusTrigger option
      const shouldAttachFocus = focusTrigger === 'always' || focusTrigger === 'auto' && isFocusable(el);
      if (shouldAttachFocus) {
        el.addEventListener('focusin', handleFocusIn);
        el.addEventListener('focusout', handleFocusOut);
      }

      // Attach keydown for Escape handling
      el.addEventListener('keydown', handleKeyDown);
    }
    triggerRef.current = el;
  }, [focusTrigger, handleMouseEnter, handleMouseLeave, handleFocusIn, handleFocusOut, handleKeyDown]);

  // Combined ref - shorthand for calling both positionRef and interactionRef
  const ref = useCallback(el => {
    layer.ref(el);
    interactionRef(el);
  }, [layer, interactionRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);

  // Show on mount when isDefaultOpen is true
  useEffect(() => {
    if (isDefaultOpen) {
      layer.show();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- intentionally only on mount
  }, []);

  // Controlled open state — overrides hover/focus triggers
  useEffect(() => {
    if (isOpen === undefined) {
      return;
    }
    if (isOpen) {
      clearTimeouts();
      layer.show();
    } else {
      clearTimeouts();
      layer.hide();
    }
  }, [isOpen, clearTimeouts, layer]);

  // Render function that wraps layer.render with hover card behavior
  const renderHoverCard = useCallback((children, props) => {
    const renderPlacement = props?.placement ?? placement;
    const themeClassName = themeProps('hovercard').className;
    const renderProps = {
      placement: renderPlacement,
      alignment: props?.alignment ?? alignment,
      offset: spacingVars['--spacing-1'],
      // A named dialog when a label is provided; otherwise a group. A group
      // may validly be unnamed, an unnamed dialog may not — and hover cards
      // are non-modal, so group is honest semantics without a name.
      role: label ? 'dialog' : 'group',
      'aria-label': label || undefined,
      // Consumer surface style props land on the layer container — the
      // themed surface (bg/radius/shadow) where the theme class lives — so
      // customizing the card targets the same element as the theme. The inner
      // div keeps `styles.content` for padding.
      xstyle: [popoverXstyle, layerAnimations[renderPlacement], props?.xstyle],
      className: props?.className ? `${themeClassName} ${props.className}` : themeClassName,
      style: props?.style,
      // useLayer mounts only after it has verified or corrected the parent,
      // so rich HoverCard content can use block-safe markup.
      as: 'div'
    };
    return layer.render(/*#__PURE__*/_jsx("div", {
      ...{
        className: "x1lliihq x1vlblms xvmdzux x126nfab x1t818jl"
      },
      onMouseEnter: () => {
        isHoveringContentRef.current = true;
        clearTimeouts();
      },
      onMouseLeave: () => {
        isHoveringContentRef.current = false;
        scheduleHide();
      },
      onKeyDown: e => {
        if (e.key === 'Escape') {
          // Stop propagation so parent components don't react to the same Escape
          e.stopPropagation();
          // Set flag to prevent re-show when we refocus trigger
          isEscapeDismissingRef.current = true;
          // Hide immediately
          clearTimeouts();
          layer.hide();
          // Refocus the trigger
          triggerRef.current?.focus();
        }
      },
      onBlur: e => {
        // Check if focus is moving back to the trigger or staying within content
        const relatedTarget = e.relatedTarget;
        const popoverElement = e.currentTarget;

        // If focus stays within the hover card, do nothing
        if (popoverElement.contains(relatedTarget)) {
          return;
        }

        // If focus is moving back to the trigger, do nothing
        if (triggerRef.current?.contains(relatedTarget)) {
          return;
        }

        // Focus is leaving the hover card entirely
        scheduleHide();
      },
      children: children
    }), renderProps);
  }, [layer, placement, alignment, label, clearTimeouts, scheduleHide, popoverXstyle]);
  return {
    ref,
    positionRef: layer.ref,
    interactionRef,
    anchorId: layer.anchorId,
    describedBy: layer.id,
    renderHoverCard,
    show: layer.show,
    hide: layer.hide
  };
}